import { stripHtml } from './customerStoreOrders.helpers';
import type {
    FetchStoreOrderListResult,
    StoreOrderDetailView,
    StoreOrderLineItem,
    StoreOrderListSummary,
    StoreOrderQuickFilterOption,
    StoreOrderStatusMessage,
    StoreOrderTimeFilterOption,
} from './customerStoreOrders.types';

const unwrapPayload = (raw: unknown): Record<string, unknown> => {
    if (!raw || typeof raw !== 'object') {
        return {};
    }
    const root = raw as Record<string, unknown>;
    const data = root.data;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        return data as Record<string, unknown>;
    }
    return root;
};

const mapStatusMessages = (messages: unknown): StoreOrderStatusMessage[] | undefined => {
    if (!Array.isArray(messages)) {
        return undefined;
    }
    const mapped = messages
        .map((m) => {
            if (typeof m === 'string') {
                return { text: m };
            }
            const item = m as Record<string, unknown>;
            const text = String(item.text ?? '').trim();
            if (!text) {
                return null;
            }
            return {
                text,
                colorCode: item.textColorCode ? String(item.textColorCode) : undefined,
            };
        })
        .filter(Boolean) as StoreOrderStatusMessage[];
    return mapped.length ? mapped : undefined;
};

export const mapCustomerOrderSummaryListResponse = (raw: unknown): FetchStoreOrderListResult => {
    const payload = unwrapPayload(raw);
    const orders = Array.isArray(payload.orders) ? payload.orders : [];
    const timeFilterList = (Array.isArray(payload.timeFilterList) ? payload.timeFilterList : []).map(
        (item: Record<string, unknown>) => ({
            key: String(item.key ?? ''),
            value: String(item.value ?? ''),
        })
    ) as StoreOrderTimeFilterOption[];
    const quickFilterList = (Array.isArray(payload.quickFilterList) ? payload.quickFilterList : []).map(
        (item: Record<string, unknown>) => ({
            orderListType: Number(item.orderListType ?? 0),
            text: String(item.text ?? ''),
            colorCode: item.colorCode ? String(item.colorCode) : undefined,
            redirectButtonMessage: item.redirectButtonMessage as string | undefined,
        })
    ) as StoreOrderQuickFilterOption[];

    const data: StoreOrderListSummary[] = orders.map((order: Record<string, unknown>, index: number) => {
        const orderNumber = String(order.orderNumber ?? order.orderNo ?? index);
        return {
            rowId: String(order.orderId ?? orderNumber),
            orderNumber,
            orderId: (order.orderId as string | number) ?? orderNumber,
            orderDate: String(order.orderDate ?? ''),
            totalPriceStr: String(order.totalPriceStr ?? order.totalPrice ?? '-'),
            storeName: String(order.storeName ?? '-'),
            storeCode: order.storeCode ? String(order.storeCode) : undefined,
            statusMessages: mapStatusMessages(order.statusMessages),
        };
    });

    return {
        data,
        count: Number(payload.totalOrderCount ?? data.length),
        timeFilterList,
        quickFilterList,
    };
};

const mapShipmentItem = (item: Record<string, unknown>): StoreOrderLineItem => {
    const hasDiscounted = item.hasDiscounted === true;
    const productName = String(item.modelTitle ?? item.categoryName ?? item.modelCode ?? '-');
    return {
        id: String(item.orderItemId ?? item.productId ?? Math.random()),
        brand: item.newBrandName
            ? String(item.newBrandName)
            : item.brandName
              ? String(item.brandName)
              : undefined,
        productName: productName === '-' && !item.modelTitle ? '—' : productName,
        size: item.size ? String(item.size) : undefined,
        color: item.color ? String(item.color) : undefined,
        quantity: Number(item.quantity ?? 1),
        imageUrl: item.icon ? String(item.icon) : undefined,
        listPrice: hasDiscounted && item.unitPrice != null ? Number(item.unitPrice) : undefined,
        salePrice: item.salePrice != null ? Number(item.salePrice) : undefined,
        listPriceStr: hasDiscounted && item.unitPriceStr ? String(item.unitPriceStr) : undefined,
        salePriceStr: String(item.salePriceStr ?? item.unitPriceStr ?? ''),
        badgeText: item.badgeText ? String(item.badgeText) : undefined,
    };
};

const getShipmentOrderTypeKey = (shipment: Record<string, unknown>): string => {
    const orderType = shipment.orderType as Record<string, unknown> | undefined;
    return String(orderType?.key ?? orderType?.value ?? 'ORDER');
};

const collectShipmentItems = (
    detailData: Record<string, unknown>
): { orderItems: StoreOrderLineItem[]; returnItems: StoreOrderLineItem[] } => {
    const orderItems: StoreOrderLineItem[] = [];
    const returnItems: StoreOrderLineItem[] = [];
    const shipmentDetails = detailData.orderShipmentDetails;
    if (!Array.isArray(shipmentDetails)) {
        return { orderItems, returnItems };
    }

    shipmentDetails.forEach((group: Record<string, unknown>) => {
        const shipments = Array.isArray(group.shipments) ? group.shipments : [];
        shipments.forEach((shipment: Record<string, unknown>) => {
            const typeKey = getShipmentOrderTypeKey(shipment);
            const items = shipment.shipmentItems;
            if (!Array.isArray(items)) {
                return;
            }
            const target = typeKey === 'RETURN_ORDER' ? returnItems : orderItems;
            items.forEach((rawItem) => {
                target.push(mapShipmentItem(rawItem as Record<string, unknown>));
            });
        });
    });

    return { orderItems, returnItems };
};

const resolveStoreFromShipments = (
    detailData: Record<string, unknown>
): { storeName?: string; storeAddress?: string; storeCode?: string; returnInfoHtml?: string } => {
    const shipmentDetails = detailData.orderShipmentDetails;
    if (!Array.isArray(shipmentDetails)) {
        return {};
    }
    for (const group of shipmentDetails) {
        const shipments = Array.isArray((group as Record<string, unknown>).shipments)
            ? (group as Record<string, unknown>).shipments
            : [];
        for (const shipment of shipments as Record<string, unknown>[]) {
            if (getShipmentOrderTypeKey(shipment) !== 'RETURN_ORDER') {
                return {
                    storeName: shipment.storeInfo ? String(shipment.storeInfo) : undefined,
                    storeAddress: shipment.storeAddress ? String(shipment.storeAddress) : undefined,
                    storeCode: shipment.storeCode ? String(shipment.storeCode) : undefined,
                    returnInfoHtml: shipment.storeReturnInfoMessage
                        ? String(shipment.storeReturnInfoMessage)
                        : undefined,
                };
            }
        }
    }
    return {};
};

export const resolveIsReceiptDownloadActive = (detailData: Record<string, unknown>): boolean => {
    const shipmentDetails = detailData.orderShipmentDetails;
    if (!Array.isArray(shipmentDetails)) {
        return Boolean(detailData.isReceiptDownloadActive);
    }
    for (const group of shipmentDetails) {
        const shipments = Array.isArray((group as Record<string, unknown>).shipments)
            ? (group as Record<string, unknown>).shipments
            : [group];
        for (const shipment of shipments as Record<string, unknown>[]) {
            const shipmentDetail = shipment.shipmentDetail as Record<string, unknown> | undefined;
            const component = (shipmentDetail?.shipmentComponent ?? shipment.shipmentComponent) as
                | Record<string, unknown>
                | undefined;
            if (component?.isReceiptDownloadActive === true) {
                return true;
            }
        }
    }
    return false;
};

const mapDiscountLinesFromOrderSummary = (payment: Record<string, unknown>) => {
    const orderSummary = payment.orderSummary as Record<string, unknown> | undefined;
    const list = orderSummary?.orderSummaryList;
    if (!Array.isArray(list)) {
        return undefined;
    }
    const discountRow = list.find((row) => (row as Record<string, unknown>).summaryType === 2) as
        | Record<string, unknown>
        | undefined;
    const detailSummary = discountRow?.detailSummary as Record<string, unknown> | undefined;
    const detailList = detailSummary?.detailSummaryList;
    if (!Array.isArray(detailList)) {
        return undefined;
    }
    return detailList.map((d: Record<string, unknown>) => ({
        label: stripHtml(String(d.detailTitle ?? '-')),
        amount: 0,
        amountStr: stripHtml(String(d.detailValue ?? '')),
    }));
};

const mapPaymentBlock = (payment: Record<string, unknown>) => {
    const methods = Array.isArray(payment.orderPaymentMethods) ? payment.orderPaymentMethods : [];
    const firstMethod = (methods[0] as Record<string, unknown>) ?? {};
    const creditInfos = Array.isArray(firstMethod.creditCardInfos) ? firstMethod.creditCardInfos : [];
    const firstCredit = (creditInfos[0] as Record<string, unknown>) ?? {};

    const orderSummary = payment.orderSummary as Record<string, unknown> | undefined;
    const summaryList = Array.isArray(orderSummary?.orderSummaryList) ? orderSummary.orderSummaryList : [];
    const grandRow = summaryList.find((r) => (r as Record<string, unknown>).summaryType === 13) as
        | Record<string, unknown>
        | undefined;
    const subTotalRow = summaryList.find((r) => (r as Record<string, unknown>).summaryType === 1) as
        | Record<string, unknown>
        | undefined;
    const discountRow = summaryList.find((r) => (r as Record<string, unknown>).summaryType === 2) as
        | Record<string, unknown>
        | undefined;

    return {
        payment: {
            methodLabel: String(firstMethod.paymentMethodTitle ?? '-'),
            bankLabel: firstCredit.installmentsText ? String(firstCredit.installmentsText) : undefined,
            amount: firstMethod.amount != null ? Number(firstMethod.amount) : Number(payment.totalPrice ?? 0),
            amountStr: String(firstMethod.amountStr ?? payment.totalPriceStr ?? ''),
        },
        summary: {
            subTotal: payment.price != null ? Number(payment.price) : undefined,
            subTotalStr: subTotalRow
                ? stripHtml(String(subTotalRow.summaryValue ?? ''))
                : payment.priceStr
                  ? String(payment.priceStr)
                  : undefined,
            discountTotal: payment.discount != null ? Number(payment.discount) : undefined,
            discountTotalStr: discountRow
                ? stripHtml(String(discountRow.summaryValue ?? ''))
                : payment.discountStr
                  ? String(payment.discountStr)
                  : undefined,
            discountLines: mapDiscountLinesFromOrderSummary(payment),
            grandTotal: payment.totalPrice != null ? Number(payment.totalPrice) : undefined,
            grandTotalStr: grandRow
                ? stripHtml(String(grandRow.summaryValue ?? ''))
                : payment.totalPriceStr
                  ? String(payment.totalPriceStr)
                  : undefined,
        },
    };
};

export const mapCustomerStoreOrderDetailResponse = (raw: unknown): StoreOrderDetailView | null => {
    const root = raw as Record<string, unknown>;
    if (root.isSucceed === false) {
        return null;
    }
    const detailData = unwrapPayload(raw);
    const orderInfo = (detailData.orderInfoDetail as Record<string, unknown>) ?? {};
    const payment = (detailData.orderPaymentDetail as Record<string, unknown>) ?? {};

    const orderNumber = String(orderInfo.orderNumber ?? detailData.orderNumber ?? '');
    const orderDate = String(orderInfo.orderDate ?? detailData.orderDate ?? '');
    const orderDateStr = orderInfo.orderDateStr ? String(orderInfo.orderDateStr) : undefined;

    const { orderItems, returnItems } = collectShipmentItems(detailData);
    const storeMeta = resolveStoreFromShipments(detailData);
    const { payment: paymentView, summary } = mapPaymentBlock(payment);

    return {
        orderNumber,
        orderDate,
        orderDateStr,
        storeName: storeMeta.storeName,
        storeAddress: storeMeta.storeAddress,
        storeCode: storeMeta.storeCode,
        items: orderItems,
        returnItems: returnItems.length ? returnItems : undefined,
        returnExchangeInfoHtml: storeMeta.returnInfoHtml,
        payment: paymentView,
        summary,
        isReceiptDownloadActive: resolveIsReceiptDownloadActive(detailData),
        rawDetail: detailData,
    };
};
